import Typography from '@components/v2/ui/Typography/Typography';
import { Box, LinearProgress } from '@mui/material';
import { Virtuoso } from 'react-virtuoso';
import LangEnum from '../../../../../lang/LangEnum';
import { TargetStatus, TargetUnit } from '../../../../../services/modules/target/target.types';
import TargetConversionCell from '../../../../target/components/TargetConversionCell/TargetConversionCell';
import { OrderTargetBreakdown, OrderTargetBreakdownListRow } from '../../orderTargets.types';
import OrderTargetsLoadError from '../OrderTargetsLoadError';
import { orderTargetBreakdownListComponents } from './orderTargetBreakdownList.components';
import useOrderTargetBreakdownListStyles, {
  ROW_PITCH,
  VISIBLE_ROWS,
} from './orderTargetBreakdownList.styles';

interface OrderTargetBreakdownListProps {
  rows: OrderTargetBreakdownListRow[];
  amountsMinChars: number;
  unit: TargetUnit;
  targetStatus: TargetStatus;
  breakdown: Pick<OrderTargetBreakdown, 'isLoading' | 'isError' | 'reload'>;
}

const OrderTargetBreakdownList = ({
  rows,
  amountsMinChars,
  unit,
  targetStatus,
  breakdown,
}: OrderTargetBreakdownListProps) => {
  const { classes } = useOrderTargetBreakdownListStyles({ amountsMinChars });

  if (breakdown.isError) {
    return (
      <OrderTargetsLoadError
        message={LangEnum.TARGET_OBJECT_ITEMS_ERROR}
        onRetry={breakdown.reload}
      />
    );
  }

  if (breakdown.isLoading) {
    return (
      <Box className={classes.loading}>
        <LinearProgress />
      </Box>
    );
  }

  if (rows.length === 0) {
    return null;
  }

  return (
    <Virtuoso
      data={rows}
      context={{ amountsMinChars }}
      fixedItemHeight={ROW_PITCH}
      className={classes.scroller}
      style={{ height: Math.min(rows.length, VISIBLE_ROWS) * ROW_PITCH }}
      components={orderTargetBreakdownListComponents}
      computeItemKey={(_, row) => row.id}
      itemContent={(_, row) => (
        <>
          <Typography variant="body2" color="textSecondary" noWrap>
            {row.label}
          </Typography>
          <Typography variant="body2" color="textSecondary" className={classes.amounts}>
            {row.amountsLabel}
          </Typography>
          <Box className={classes.conversion}>
            <TargetConversionCell
              attainment={row.attainment}
              unit={unit}
              targetStatus={targetStatus}
              labelPosition="left"
              labelColor="text.secondary"
              labelVariant="body2"
            />
          </Box>
        </>
      )}
    />
  );
};

export default OrderTargetBreakdownList;
